import React, { useEffect, useRef, useState } from 'react';
import './DeluxeEmbeddedPaymentForm.css';

const SCRIPT_ID = 'deluxe-embedded-payments-sdk';

function readDeluxeSdk() {
  try {
    return new Function(
      'return typeof EmbeddedPayments !== "undefined" ? EmbeddedPayments : (typeof DigitalWalletsPay !== "undefined" ? DigitalWalletsPay : null)'
    )();
  } catch {
    return null;
  }
}

function loadEmbeddedScript(scriptUrl) {
  return new Promise((resolve, reject) => {
    const ready = readDeluxeSdk();
    if (ready?.init) {
      resolve(ready);
      return;
    }
    const finish = () => {
      const sdk = readDeluxeSdk();
      if (sdk?.init) resolve(sdk);
      else reject(new Error('Deluxe payment form is unavailable'));
    };
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      if (existing.dataset.loaded === 'true') {
        finish();
        return;
      }
      existing.addEventListener('load', finish, { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load Deluxe payment form')), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = scriptUrl;
    script.async = true;
    script.onload = () => {
      script.dataset.loaded = 'true';
      finish();
    };
    script.onerror = () => reject(new Error('Failed to load Deluxe payment form'));
    document.head.appendChild(script);
  });
}

function asRecord(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function extractPaymentId(data) {
  const payload = asRecord(data?.data || data);
  return String(
    payload.paymentId ||
      payload.PaymentId ||
      payload.transactionId ||
      payload.TransactionId ||
      payload.id ||
      data?.paymentId ||
      ''
  ).trim();
}

function extractSubscriptionId(data) {
  const payload = asRecord(data?.data || data);
  const recurring = asRecord(payload.recurring || payload.subscription);
  return String(
    payload.subscriptionId ||
      payload.SubscriptionId ||
      recurring.subscriptionId ||
      recurring.id ||
      data?.subscriptionId ||
      ''
  ).trim();
}

function extractVault(data) {
  const payload = asRecord(data?.data || data);
  const customerId = payload.customerId || payload.CustomerToken || payload.customerToken || data?.customerId;
  const paymentMethodId =
    payload.vaultId || payload.VaultId || payload.paymentMethodId || payload.Token || payload.token || data?.vaultId;
  return {
    customerId: customerId != null ? String(customerId) : '',
    paymentMethodId: paymentMethodId ? String(paymentMethodId) : '',
    last4: String(payload.MaskedPan || payload.maskedPan || payload.Token || '').replace(/\D/g, '').slice(-4),
    token: payload.Token || payload.token || '',
  };
}

const DeluxeEmbeddedPaymentForm = ({
  jwt,
  scriptUrl,
  mode = 'cryptogram',
  onSuccess,
  onError,
  disabled = false,
}) => {
  const containerIdRef = useRef(`deluxe-embedded-${Math.random().toString(36).slice(2, 10)}`);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;

  useEffect(() => {
    let cancelled = false;
    if (!jwt || !scriptUrl || disabled) return undefined;

    setReady(false);
    setError('');

    loadEmbeddedScript(scriptUrl)
      .then((EmbeddedPayments) => {
        if (cancelled || !EmbeddedPayments) {
          throw new Error('Deluxe payment form is unavailable');
        }
        const googlePayEnv = /payments2\.deluxe\.com/i.test(scriptUrl) ? 'TEST' : 'PRODUCTION';
        return EmbeddedPayments.init(jwt, {
          countryCode: 'US',
          currencyCode: 'USD',
          paymentMethods: ['cc'],
          merchantCapabilities: ['supports3DS'],
          allowedCardAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
          supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
          googlePayEnv,
        }).then((instance) => {
          if (cancelled) return instance;
          instance.renderProdPanel = () => {};
          const embedIframe = instance.embedIframe?.bind(instance);
          if (embedIframe) {
            instance.embedIframe = () => {
              embedIframe();
              const iframe = document.getElementById('payment-form');
              if (!iframe?.src) return;
              const raw = iframe.src.replace(/[\r\n\t]/g, '').replace(/ +/g, '');
              const url = new URL(raw, window.location.href);
              const origin = new URL(instance.baseUrl || scriptUrl, window.location.href).origin;
              const clean = new URL('/embedded/embedded/render', origin);
              url.searchParams.forEach((value, key) => {
                clean.searchParams.set(key, value.trim());
              });
              iframe.src = clean.toString();
            };
          }
          instance.setEventHandlers({
            onTxnSuccess: (_gateway, data) => {
              if (cancelled) return;
              const paymentId = extractPaymentId(data);
              if (!paymentId) {
                const message = 'Deluxe did not return a payment id';
                setError(message);
                onErrorRef.current?.(new Error(message));
                return;
              }
              const vault = extractVault(data);
              onSuccessRef.current?.({
                paymentId,
                cryptogram: paymentId,
                subscriptionId: extractSubscriptionId(data),
                customerId: vault.customerId,
                paymentMethodId: vault.paymentMethodId,
                last4: vault.last4,
              });
            },
            onTxnFailed: (_gateway, data) => {
              const message = asRecord(data).message || data?.message || 'Payment failed';
              setError(String(message));
              onErrorRef.current?.(new Error(String(message)));
            },
            onValidationError: (_gateway, errors) => {
              const message = Array.isArray(errors) ? errors.join(' ') : 'Please check the card details';
              setError(message);
            },
            onCancel: () => {},
            onTokenSuccess: (data) => {
              if (cancelled) return;
              const vault = extractVault(data);
              if (!vault.paymentMethodId && !vault.token) {
                const message = 'Deluxe did not return a saved payment method';
                setError(message);
                onErrorRef.current?.(new Error(message));
                return;
              }
              onSuccessRef.current?.(vault);
            },
            onTokenFailed: (data) => {
              const message = asRecord(data).message || 'Could not save the card';
              setError(String(message));
              onErrorRef.current?.(new Error(String(message)));
            },
          });
          return instance.render({
            containerId: containerIdRef.current,
            paymentpanelstyle: 'light',
          });
        });
      })
      .then(() => {
        if (!cancelled) setReady(true);
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err?.message || 'Could not load Deluxe payment form';
        setError(message);
        onErrorRef.current?.(err);
      });

    return () => {
      cancelled = true;
    };
  }, [jwt, scriptUrl, mode, disabled]);

  return (
    <div className="deluxe-embedded">
      {!ready && !error && <p className="deluxe-embedded__status">Loading secure card form…</p>}
      {error && (
        <div className="deluxe-embedded__error" role="alert">
          {error}
        </div>
      )}
      <div id={containerIdRef.current} className="deluxe-embedded__frame" />
    </div>
  );
};

export default DeluxeEmbeddedPaymentForm;
